import { Theme } from '@mui/material';
import {
  ConversationChannelType,
  ConversationParticipantEntity,
  ConversationParticipationMode,
} from '@provision/conversation-types';
import { useMemo } from 'react';
import { DynamicIcon } from 'src/components';
import { PillLinkChip } from 'src/components/Pill';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { Conversation, PreparedConversationParticipantOption } from 'src/graphql';
import { useConversationParticipation } from 'src/graphql/entities/conversation-participation/conversation-participation';
import { useParticipantOptionsSearch } from 'src/pages/Conversations/SidePanel/ConversationDetails/use-participant-options-search';
import { ParticipantPillItem } from './use-conversation-participant-pills';

interface ParticipantTypeAppearance {
  icon: string;
  getColor: (theme: Theme) => string;
}

const PARTICIPANT_TYPE_APPEARANCE: Record<
  ConversationParticipantEntity,
  ParticipantTypeAppearance
> = {
  [ConversationParticipantEntity.Client]: {
    icon: 'user',
    getColor: (theme) => theme.palette.warning.main,
  },
  [ConversationParticipantEntity.PlatformUser]: {
    icon: 'headphones',
    getColor: (theme) => theme.palette.custom.OV.sys.secondary,
  },
  [ConversationParticipantEntity.Rsm]: {
    icon: 'radar',
    getColor: (theme) => theme.palette.custom.OV.sys.primary,
  },
  [ConversationParticipantEntity.Unknown]: {
    icon: 'message-circle-question',
    getColor: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
  },
  [ConversationParticipantEntity.Bot]: {
    icon: 'message-circle-question',
    getColor: (theme) => theme.palette.custom.OV.sys.onSurfaceVariant,
  },
};

const isParticipantEntity = (value: string): value is ConversationParticipantEntity =>
  value in PARTICIPANT_TYPE_APPEARANCE;

const getParticipantTypeAppearance = (
  participantEntity: string,
): ParticipantTypeAppearance => {
  const entity = participantEntity.toLowerCase();

  return isParticipantEntity(entity)
    ? PARTICIPANT_TYPE_APPEARANCE[entity]
    : PARTICIPANT_TYPE_APPEARANCE[ConversationParticipantEntity.Unknown];
};

export const AddParticipantMenu = ({
  conversation,
  pills,
  onAdded,
  onAddStart,
  onAddSettled,
}: {
  conversation: Conversation;
  pills: ParticipantPillItem[];
  onAdded?: () => void;
  /** Registers an optimistic pending pill for the picked option; returns its key. */
  onAddStart?: (option: PreparedConversationParticipantOption) => string;
  /** Called once the create call has settled; only used to roll back on failure. */
  onAddSettled?: (key: string, success: boolean) => void;
}) => {
  const { createParticipationWithRelatedData } = useConversationParticipation();
  const {
    open,
    search,
    loading,
    options: participantOptions,
    allOptions,
    setSearch,
    handleOpenChange,
  } = useParticipantOptionsSearch({ partnerId: conversation.partnerId, skipClients: false });

  const existingIdentities = useMemo(() => {
    const ids = new Set<string>();
    const identities = new Set<string>();

    pills.forEach((pill) => {
      if (pill.participant.conversationParticipantId) {
        ids.add(pill.participant.conversationParticipantId);
      }
      if (pill.participant.participantIdentity) {
        identities.add(pill.participant.participantIdentity.toLowerCase());
      }
      if (pill.participant.client?.primaryEmail) {
        identities.add(pill.participant.client.primaryEmail.toLowerCase());
      }
    });

    return { ids, identities };
  }, [pills]);

  const options = useMemo<SelectMenuOption[]>(
    () =>
      participantOptions
        .filter((option) => {
          if (existingIdentities.ids.has(option.conversationParticipantId)) {
            return false;
          }
          const candidates = [option.email, option.phoneNumber, option.value]
            .filter(Boolean)
            .map((value) => value.toLowerCase());
          return !candidates.some((candidate) => existingIdentities.identities.has(candidate));
        })
        .map((option) => {
          const appearance = getParticipantTypeAppearance(option.participantEntity);

          return {
            value: option.id,
            title: option.label || option.email || option.phoneNumber || option.value,
            group: option.group,
            description: option.email || undefined,
            icon: (
              <DynamicIcon
                name={appearance.icon}
                library="lucide"
                size={14}
                sx={{ color: appearance.getColor }}
              />
            ),
            keywords: [option.email, option.phoneNumber].filter(Boolean),
          };
        }),
    [participantOptions, existingIdentities],
  );

  const addParticipant = async (value: string) => {
    const selected = allOptions.find((option) => option.id === value);
    if (!selected) {
      return;
    }

    let participationMode = ConversationParticipationMode.Assist;
    const channels = [
      { channelType: ConversationChannelType.Chat, externalIdentifier: selected.id },
    ];

    if (selected.participantEntity === ConversationParticipantEntity.Client) {
      participationMode = ConversationParticipationMode.Engage;

      if (selected.email) {
        channels.push({
          channelType: ConversationChannelType.Email,
          externalIdentifier: selected.email,
        });
      }
    }

    const pendingKey = onAddStart?.(selected);
    handleOpenChange(false);

    try {
      const created = await createParticipationWithRelatedData({
        participantEntity: selected.participantEntity,
        participationMode,
        entityIdentifier: selected.id,
        channels,
        conversationId: conversation.id,
        partnerId: conversation.partnerId,
        partnerEmail:
          conversation.partner.brandGroups?.[0]?.supportEmail ??
          conversation.partner.supportEmail,
        conversation,
        onSuccess: onAdded,
      });

      // Only roll back on failure — on success the pending pill is superseded
      // once the refetched participation records land (see use-participant-pill-actions).
      // The mutation reports its own failure alert and resolves undefined instead of throwing.
      if (!created && pendingKey) {
        onAddSettled?.(pendingKey, false);
      }
    } catch {
      if (pendingKey) {
        onAddSettled?.(pendingKey, false);
      }
    }
  };

  return (
    <SelectMenu
      options={options}
      onChange={(value) => void addParticipant(value)}
      variant="inline"
      sectioned
      search
      searchPlaceholder="Search agents & clients…"
      searchValue={search}
      onSearchChange={setSearch}
      loading={loading}
      open={open}
      onOpenChange={handleOpenChange}
      triggerLabel="+ Add"
      placeholder="+ Add"
      menuWidth="21.875rem"
      label="Add participant"
      renderTrigger={({ getTriggerProps }) => (
        <PillLinkChip {...getTriggerProps()} type="button">
          + Add
        </PillLinkChip>
      )}
    />
  );
};
